/**
 * SessionManager - Token refresh logic with retry and exponential backoff
 */

import {
  getRefreshCookie,
  getAuthCookie,
  setAuthCookie,
  setRefreshCookie,
  getRememberMeCookie,
  isTokenExpired,
} from '../cookieAuth'
import {
  API_BASE,
  MAX_REFRESH_RETRIES,
  REFRESH_RETRY_BASE_DELAY,
  isValidJWTStructure,
  sleep,
  type RefreshOutcome,
} from './constants'
import { decodeJwtUserId } from './tokenSync'
import { isRefreshTokenDead, markRefreshTokenDead } from './deadRefreshRegistry'

export interface TokenRefreshCallbacks {
  onTokenRefreshed?: (newToken: string) => void
  syncTokenToLocalStorage: (token: string) => void
  broadcastTokenSync: (token: string) => void
}

/**
 * Attempt token refresh (v1 SimpleJWT) with exponential backoff retries.
 *
 * Returns a {@link RefreshOutcome}:
 *   'refreshed'   — a fresh access token was minted and stored.
 *   'dead'        — the refresh endpoint answered 401/403 (token expired or
 *                   revoked). Justifies a sign-out on its own.
 *   'transient'   — offline, network error, timeout, 5xx/429, or a
 *                   concurrent refresh. Preserve the session; retry later.
 *   'no_material' — no JS-readable refresh credential exists for this v1
 *                   leg (HttpOnly under Auth v2, or simply logged out).
 *                   Retrying can never change this verdict, and it must
 *                   not veto a definitive 'dead' from the master path
 *                   (BUG-2026-06-11-ZOMBIE-SESSION — see constants.ts).
 *
 * BUG-2026-06-04-LOGOUT-EVERY-HOUR: previously this returned a boolean and
 * any non-success (incl. a transient 5xx or a missing-because-HttpOnly
 * cookie) bubbled up to `handleSessionDeath`, signing the user out roughly
 * every access-token TTL. We now only report 'dead' on a definitive 401/403.
 */
export async function refreshTokenWithRetry(
  callbacks: TokenRefreshCallbacks,
  isRefreshingRef: { current: boolean },
): Promise<RefreshOutcome> {
  if (isRefreshingRef.current) {
    console.log('[@quotech/auth] Refresh already in progress, skipping')
    return 'transient'
  }

  if (typeof navigator !== 'undefined' && !navigator.onLine) {
    console.log('[@quotech/auth] Offline - preserving session without refresh')
    return 'transient'
  }

  isRefreshingRef.current = true

  if (typeof window !== 'undefined') {
    window.dispatchEvent(new CustomEvent('quotech:token_refreshing'))
  }

  let retryCount = 0

  try {
    while (retryCount < MAX_REFRESH_RETRIES) {
      if (typeof navigator !== 'undefined' && !navigator.onLine) {
        console.log('[@quotech/auth] Went offline during refresh - preserving session')
        return 'transient'
      }

      const outcome = await performSingleRefresh(callbacks)
      if (outcome === 'refreshed') return 'refreshed'
      // A definitive 401/403 will not change on retry — stop and report it.
      if (outcome === 'dead') return 'dead'
      // No refresh material at all — retrying cannot conjure a credential.
      if (outcome === 'no_material') return 'no_material'

      retryCount++
      if (retryCount < MAX_REFRESH_RETRIES) {
        const delay = REFRESH_RETRY_BASE_DELAY * Math.pow(2, retryCount - 1)
        console.log(`[@quotech/auth] Refresh transient failure, retrying in ${delay}ms (attempt ${retryCount}/${MAX_REFRESH_RETRIES})`)
        await sleep(delay)
      }
    }

    // Exhausted retries without a definitive verdict — preserve the session.
    return 'transient'
  } finally {
    isRefreshingRef.current = false
  }
}

/**
 * Execute a single token refresh attempt against the backend.
 * Returns a {@link RefreshOutcome} (see `refreshTokenWithRetry`).
 */
async function performSingleRefresh(callbacks: TokenRefreshCallbacks): Promise<RefreshOutcome> {
  // Prefer the refresh token THIS login wrote to localStorage over the
  // domain-shared cookie (BUG-2026-06-26-STALE-COOKIE-JAR-LOGOUT family).
  // The `.quotech.ai` `quotech_refresh` cookie can be SHADOWED by a prior
  // account's leftover cookie at a different domain/path scope that our
  // writer (`setSharedRefreshCookie`, domain=.quotech.ai path=/) cannot
  // overwrite — so reading the cookie alone resurrects a dead foreign token
  // (prod: a fresh user-51 login still POSTed user-15's blacklisted refresh).
  // `localStorage.refreshToken` is always the current login's credential; the
  // cookie is only a cross-hub fallback. The cross-identity guard below still
  // applies to whichever source wins.
  const refreshToken = (typeof localStorage !== 'undefined'
    ? localStorage.getItem('refreshToken')
    : null) || getRefreshCookie()
  if (!refreshToken) {
    // No JS-readable refresh cookie. Under Auth v2 the refresh cookie is
    // HttpOnly (invisible to JS) — its absence here does NOT prove the v2
    // credential is dead (the silent master refresh is the authority for
    // v2), but it DOES prove this v1 leg has nothing to try. Report
    // 'no_material' so the SessionManager can act on a definitive 'dead'
    // from the master path instead of preserving a zombie session forever
    // (BUG-2026-06-11-ZOMBIE-SESSION).
    console.log('[@quotech/auth] No JS-readable refresh token (v2 HttpOnly?) - no_material')
    return 'no_material'
  }
  if (!isValidJWTStructure(refreshToken)) {
    // Garbage mirror can never refresh successfully — same as absent.
    console.log('[@quotech/auth] Malformed refresh token mirror - no_material')
    return 'no_material'
  }

  // Dead-token tombstone (BUG-2026-06-29-REFRESH-401-LOOP): if THIS exact
  // refresh token already 401/403'd once (recorded by any refresher in any
  // tab), never POST it again — it will only 401 forever and keep the
  // session in a loop. Treat as 'no_material' (nothing valid to try) rather
  // than 'dead', so a stale tombstoned credential cannot itself force a
  // sign-out of an otherwise-recoverable session.
  if (isRefreshTokenDead(refreshToken)) {
    console.log('[@quotech/auth] Refresh token previously rejected (tombstoned) - no_material')
    return 'no_material'
  }

  // A v1 refresh token is a 30-day JWT; if it is already past its own `exp`
  // the server can only answer 401 ("token is expired" — prod fp 0419ee46c4,
  // user_id=32). Detect it client-side, tombstone it, and report 'dead' with
  // ZERO network round-trips so the loop stops immediately and the session
  // signs out cleanly. (Unlike the access-token TTL churn of
  // BUG-2026-06-04-LOGOUT-EVERY-HOUR, an expired 30-day refresh token is a
  // genuinely ended session — 'dead' is the correct, non-flapping verdict.)
  if (isTokenExpired(refreshToken)) {
    console.log('[@quotech/auth] Refresh token itself is expired - dead (no network call)')
    markRefreshTokenDead(refreshToken)
    return 'dead'
  }

  // Cross-identity guard (BUG-2026-06-26-STALE-COOKIE-JAR-LOGOUT): never POST
  // a refresh token that belongs to a DIFFERENT account than the live bearer.
  // A prior user's leftover refresh token (revoked/blacklisted after they
  // signed out) would answer 401 and be read as a definitive 'dead', signing
  // out the CURRENT, valid user. This leg has nothing valid to try for the
  // current identity → 'no_material' (which, unlike 'dead', never forces a
  // sign-out on its own). Indeterminate ids (null) fall through unchanged so
  // legitimate first-refresh / non-Quotech tokens are never blocked.
  // Anchor identity on the bearer the API actually uses: localStorage first,
  // then the domain-shared `quotech_auth` access cookie (BUG-2026-06-26-COOKIE-
  // BEARER-NOT-ANCHORED). A cookie-bearer / localStorage-empty session (some
  // SSO paths) must STILL recognise — and refuse — a prior account's leftover
  // refresh token, or this guard fails open and POSTs the foreign token → 401.
  const liveBearer = ((typeof localStorage !== 'undefined')
    ? (localStorage.getItem('accessTokennew')
      || localStorage.getItem('IdToken')
      || localStorage.getItem('accessToken'))
    : null) || getAuthCookie()
  const bearerId = decodeJwtUserId(liveBearer)
  const refreshId = decodeJwtUserId(refreshToken)
  if (bearerId && refreshId && bearerId !== refreshId) {
    console.warn(
      `[@quotech/auth] Refresh token belongs to a different account `
      + `(refresh=${refreshId}, bearer=${bearerId}) — not sending it; no_material.`,
    )
    return 'no_material'
  }

  if (typeof navigator !== 'undefined' && !navigator.onLine) {
    console.log('[@quotech/auth] Offline - skipping token refresh, session preserved')
    return 'transient'
  }

  try {
    const controller = new AbortController()
    const timeoutId = setTimeout(() => controller.abort(), 10000)

    const response = await fetch(`${API_BASE}/auth/refresh-token/`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ refresh: refreshToken }),
      signal: controller.signal,
    })

    clearTimeout(timeoutId)

    if (!response.ok) {
      console.log(`[@quotech/auth] Refresh returned ${response.status}`)
      // Only a 401/403 is a definitively dead refresh token. A 5xx/429 is a
      // server hiccup — preserve the session and retry.
      if (response.status === 401 || response.status === 403) {
        // Tombstone this exact token so no refresher (this tick's retries,
        // a sibling tab, the axios 401 interceptor, or apiClient) re-POSTs
        // it — that endless re-POST is BUG-2026-06-29-REFRESH-401-LOOP.
        markRefreshTokenDead(refreshToken)
        return 'dead'
      }
      return 'transient'
    }

    const data = await response.json()

    if (!data.access || !isValidJWTStructure(data.access)) {
      console.log('[@quotech/auth] Received invalid access token from refresh')
      return 'transient'
    }

    const rememberMe = getRememberMeCookie()
    const accessExpiry = rememberMe ? 30 : 7
    const refreshExpiry = rememberMe ? 90 : 30

    setAuthCookie(data.access, accessExpiry)
    if (data.refresh && isValidJWTStructure(data.refresh)) {
      setRefreshCookie(data.refresh, refreshExpiry)
    }

    callbacks.syncTokenToLocalStorage(data.access)
    callbacks.broadcastTokenSync(data.access)
    callbacks.onTokenRefreshed?.(data.access)

    if (typeof window !== 'undefined') {
      window.dispatchEvent(new CustomEvent('quotech:token_refreshed', { detail: { token: data.access } }))
    }

    console.log('[@quotech/auth] Token refreshed successfully')
    return 'refreshed'
  } catch (error) {
    // Network error / timeout / abort — the refresh token may still be
    // alive; preserve the session and let the caller retry.
    if (error instanceof Error && error.name === 'AbortError') {
      console.warn('[@quotech/auth] Token refresh timed out - preserving session')
    } else {
      console.warn('[@quotech/auth] Network error during refresh - preserving session', error)
    }
    return 'transient'
  }
}
